/**
 * UrlParams Utility Class.
 *
 * Abstracts parsing of current window url parameters.
 *
 */
// imports
import { safeLocationSearch } from './safeglobals';

export type ParsedUrlParams = Record<string, Array<string>>;

/**
 * UrlParams Utility Class.
 */
export class UrlParams {
        

    public static getOneOf(keys: Array<string>, params: ParsedUrlParams) : Array<string> 
    {
        for(let i = 0; i < keys.length; i ++) {
            const key = keys[i];
            const result = UrlParams.get(key, params);
            if (result.length) {
                return result;
            }
        }
        return [];
    }

    public static get(key: string, params: ParsedUrlParams ) : Array<string> 
    {
        // own keys only: `constructor`, `toString` and the like are inherited, not parameters
        if (!Object.prototype.hasOwnProperty.call(params, key)) {
            return [];
        }
        return params[key];
    }

    /**
     * Parse the current url's parameters into an object: key to every value, in
     * URL order. Each key is also recorded under its lower-case form, so `GCLID`
     * is found as `gclid`.
     *
     * Read the way URLSearchParams reads a query (3.0.0, BACKLOG.md §9.13 row 14):
     * `+` is a space, escapes are decoded, and the value is everything after the
     * first `=`. A value is never ASCII-stripped once decoded: browsers report the
     * query percent-encoded, so the old strip never removed anything, and doing it
     * after decoding would turn `café` into `caf`.
     *
     * A parameter with an empty value (`?gclid=`) is absent (row 8): every check
     * downstream means "present and non-empty", and `0` is a value.
     *
     * @return {Dictionary}
     */
    public static parse(): ParsedUrlParams
    {
        const params: Record<string, Array<string>> = {};
        const search = UrlParams.getWindowSearch().substring(1);
        const pairs = search.split('&');
        for (let i = 0; i < pairs.length; i++) {
            const pair = pairs[i];
            const at = pair.indexOf('=');
            if (at === -1) {
                continue;
            }
            const key = UrlParams.decode(pair.substring(0, at));
            // One occurrence is one value. A comma is part of the value, not a
            // separator (BACKLOG.md §9.15 D11(b)). Repeated keys append.
            const value = UrlParams.decode(pair.substring(at + 1));
            if (key === '' || value === '') {
                continue;
            }
            UrlParams.append(params, key, value);
            const lower = key.toLowerCase();
            if (lower !== key) {
                // append, never reset: `gclid=a&GCLID=b` keeps `a` first
                UrlParams.append(params, lower, value);
            }
        }
        return params;
    }

    protected static append(params: ParsedUrlParams, key: string, value: string) : void
    {
        if (!Object.prototype.hasOwnProperty.call(params, key)) {
            // defined, never assigned: assigning `__proto__` on a plain object invokes the
            // legacy setter, which re-parents the map instead of adding the parameter
            Object.defineProperty(params, key, { value: [], writable: true, enumerable: true, configurable: true });
        }
        params[key].push(value);
    }

    /**
     * `+` to space, then decode each run of escapes on its own, so one malformed
     * escape (`100%`, invalid UTF-8) stays as written instead of costing the
     * whole value, which is also what URLSearchParams does.
     */
    protected static decode(raw: string) : string
    {
        return raw.replace(/\+/g, ' ').replace(/(%[0-9A-Fa-f]{2})+/g, (run: string) => {
            try {
                return decodeURIComponent(run);
            } catch (error) {
                return run;
            }
        });
    }

    /**
     * Getter that adds support for mocking the window search
     * (query string) for testing purposes.
     *
     * @return {tring}
     */
    protected static getWindowSearch(): string {
        if (typeof(window.__mock_window_search) === 'string') {
            return window.__mock_window_search;
        } else {
            return safeLocationSearch();
        }
    }
}
